import { animationFormats, getFileBaseName } from "./imageFormats";
import type { ResultImage, SourceImage } from "./imageTypes";
import { createEncoderWorker } from "./workerClients";
import type { AnimationEncodeRequest, AnimationEncodeResponse } from "../workers/encoder.types";

type FFmpegModule = typeof import("@ffmpeg/ffmpeg");
type FFmpegInstance = InstanceType<FFmpegModule["FFmpeg"]>;
type AnimationFormatKey = (typeof animationFormats)[number]["key"];

interface CreateAnimationsOptions {
  sources: SourceImage[];
  frameDelay: number;
  compress: boolean;
  crossfade?: boolean;
  jobId?: number;
  isJobActive: (jobId: number) => boolean;
  isFormatActive?: (key: AnimationFormatKey) => boolean;
  onProcessing: (key: AnimationFormatKey, isProcessing: boolean, jobId?: number) => void;
  onResult: (key: AnimationFormatKey, result: ResultImage, jobId?: number) => void;
  onError?: (key: AnimationFormatKey, message: string, jobId?: number) => void;
}

let ffmpegQueue: Promise<void> = Promise.resolve();
let ffmpegModule: Promise<{ ffmpeg: FFmpegInstance }> | null = null;

const enqueueFfmpeg = (task: () => Promise<void>) => {
  ffmpegQueue = ffmpegQueue.then(task, task);
  return ffmpegQueue;
};

const loadFfmpeg = async (): Promise<FFmpegInstance> => {
  if (!ffmpegModule) {
    ffmpegModule = (async () => {
      const mod = await import("@ffmpeg/ffmpeg");
      const ffmpeg = new mod.FFmpeg();
      await ffmpeg.load();
      return { ffmpeg };
    })();
  }
  const { ffmpeg } = await ffmpegModule;
  return ffmpeg;
};

export const disposeAnimationEncoders = () => {
  if (ffmpegModule) {
    ffmpegModule.then(({ ffmpeg }) => {
      try {
        ffmpeg.terminate();
      } catch {
        // FFmpeg termination can fail after the instance has already torn down.
      }
    }).catch(() => {
      // Ignore stale FFmpeg teardown failures.
    });
    ffmpegModule = null;
  }
};

const isActive = (options: CreateAnimationsOptions) =>
  options.jobId === undefined || options.isJobActive(options.jobId);

const isFormatActive = (key: AnimationFormatKey, options: CreateAnimationsOptions) =>
  isActive(options) && (!options.isFormatActive || options.isFormatActive(key));

const createAnimatedGif = async (options: CreateAnimationsOptions) => {
  const { sources, frameDelay, compress, crossfade, jobId, onProcessing, onResult, onError } = options;
  if (sources.length < 2 || !isFormatActive("gif", options)) return;

  onProcessing("gif", true, jobId);

  try {
    const sourceFrames: Array<{ width: number; height: number; data: Uint8ClampedArray }> = [];

    for (const source of sources) {
      if (!isFormatActive("gif", options)) return;
      const bitmap = await createImageBitmap(source.blob);

      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      sourceFrames.push({
        width: imgData.width,
        height: imgData.height,
        data: imgData.data,
      });
    }

    if (!isFormatActive("gif", options)) return;

    let frames = sourceFrames;
    let actualDelay = frameDelay;

    if (crossfade && sourceFrames.length >= 2) {
      actualDelay = Math.round(frameDelay / 3);
      const crossfadedFrames: Array<{ width: number; height: number; data: Uint8ClampedArray }> = [];

      for (let i = 0; i < sourceFrames.length; i++) {
        const current = sourceFrames[i];
        const next = sourceFrames[(i + 1) % sourceFrames.length];
        crossfadedFrames.push(current);

        for (const blend of [0.33, 0.66]) {
          const blendedData = new Uint8ClampedArray(current.data.length);
          for (let j = 0; j < current.data.length; j++) {
            blendedData[j] = Math.round(current.data[j] * (1 - blend) + next.data[j] * blend);
          }
          crossfadedFrames.push({
            width: current.width,
            height: current.height,
            data: blendedData,
          });
        }
      }

      frames = crossfadedFrames;
    }

    const worker = createEncoderWorker();
    const result = await new Promise<AnimationEncodeResponse>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<AnimationEncodeResponse>) => {
        worker.terminate();
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data);
        }
      };
      worker.onerror = (event) => {
        worker.terminate();
        reject(new Error(event.message || "Worker error"));
      };

      const request: AnimationEncodeRequest = {
        id: 0,
        type: "animation",
        format: "gif",
        frames,
        frameDelay: actualDelay,
        compress,
      };

      const transferables = frames.map((frame) => frame.data.buffer);
      worker.postMessage(request, transferables);
    });

    if (!isFormatActive("gif", options)) return;

    const blob = new Blob([result.buffer], { type: "image/gif" });
    const url = URL.createObjectURL(blob);
    const baseName = getFileBaseName(sources[0].name);

    onResult(
      "gif",
      {
        blob,
        url,
        name: `${baseName}.gif`,
        size: blob.size,
        originalSize: blob.size,
      },
      jobId
    );
  } catch (err) {
    console.error("Failed to create animated GIF:", err);
    onError?.("gif", err instanceof Error ? err.message : "Failed to create animated GIF", jobId);
  } finally {
    onProcessing("gif", false, jobId);
  }
};

const createFfmpegAnimations = async (options: CreateAnimationsOptions) => {
  const { sources, frameDelay, compress, crossfade, jobId, onProcessing, onResult, onError } = options;
  const formatsToEncode = animationFormats.filter((format) => format.key !== "gif");
  if (sources.length < 2 || formatsToEncode.length === 0) return;
  if (!isActive(options)) return;

  await enqueueFfmpeg(async () => {
    if (!isActive(options)) return;

    let ffmpeg: FFmpegInstance;
    try {
      ffmpeg = await loadFfmpeg();
    } catch (err) {
      console.error("Failed to load FFmpeg:", err);
      formatsToEncode.forEach((format) => {
        if (isFormatActive(format.key, options)) {
          onError?.(format.key, err instanceof Error ? err.message : "Failed to load FFmpeg", jobId);
        }
        onProcessing(format.key, false, jobId);
      });
      return;
    }

    const actualDelay = crossfade ? frameDelay / 3 : frameDelay;
    const fps = 1000 / actualDelay;
    const fpsString = Number.isFinite(fps) && fps > 0 ? fps.toFixed(3) : "2";
    const dir = `/anim_${jobId ?? "default"}_${Date.now()}`;
    const framePaths: string[] = [];

    const safeUnlink = async (path: string) => {
      try {
        await ffmpeg.deleteFile(path);
      } catch {
        // Temporary frame files may already be gone after failed encodes.
      }
    };

    try {
      try {
        await ffmpeg.createDir(dir);
      } catch {
        // Reusing an existing transient directory is harmless.
      }

      let targetWidth = 0;
      let targetHeight = 0;
      const sourceCanvases: HTMLCanvasElement[] = [];

      for (const source of sources) {
        if (!isActive(options)) return;
        const bitmap = await createImageBitmap(source.blob);
        if (!targetWidth || !targetHeight) {
          targetWidth = bitmap.width;
          targetHeight = bitmap.height;
        }

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth || bitmap.width;
        canvas.height = targetHeight || bitmap.height;
        const ctx = canvas.getContext("2d")!;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        sourceCanvases.push(canvas);
      }

      let frameIndex = 0;
      for (let i = 0; i < sourceCanvases.length; i++) {
        if (!isActive(options)) return;

        const currentCanvas = sourceCanvases[i];
        const nextCanvas = sourceCanvases[(i + 1) % sourceCanvases.length];
        const currentBlob = await new Promise<Blob>((resolve, reject) => {
          currentCanvas.toBlob((result) => {
            if (result) resolve(result);
            else reject(new Error("Failed to encode frame"));
          }, "image/png");
        });
        const currentBuffer = new Uint8Array(await currentBlob.arrayBuffer());
        const currentFilename = `${dir}/frame_${String(frameIndex++).padStart(4, "0")}.png`;
        await ffmpeg.writeFile(currentFilename, currentBuffer);
        framePaths.push(currentFilename);

        if (crossfade) {
          for (const blend of [0.33, 0.66]) {
            const blendCanvas = document.createElement("canvas");
            blendCanvas.width = targetWidth;
            blendCanvas.height = targetHeight;
            const blendCtx = blendCanvas.getContext("2d")!;
            blendCtx.globalAlpha = 1 - blend;
            blendCtx.drawImage(currentCanvas, 0, 0);
            blendCtx.globalAlpha = blend;
            blendCtx.drawImage(nextCanvas, 0, 0);

            const blendBlob = await new Promise<Blob>((resolve, reject) => {
              blendCanvas.toBlob((result) => {
                if (result) resolve(result);
                else reject(new Error("Failed to encode blend frame"));
              }, "image/png");
            });
            const blendBuffer = new Uint8Array(await blendBlob.arrayBuffer());
            const blendFilename = `${dir}/frame_${String(frameIndex++).padStart(4, "0")}.png`;
            await ffmpeg.writeFile(blendFilename, blendBuffer);
            framePaths.push(blendFilename);
          }
        }
      }

      const inputPattern = `${dir}/frame_%04d.png`;
      const buildArgs = (formatKey: string, outputPath: string) => {
        const baseArgs = ["-framerate", fpsString, "-start_number", "0", "-i", inputPattern, "-r", fpsString];

        switch (formatKey) {
          case "webp":
            return [
              ...baseArgs,
              "-loop",
              "0",
              "-pix_fmt",
              "yuva420p",
              "-lossless",
              compress ? "0" : "1",
              "-q:v",
              compress ? "60" : "95",
              "-compression_level",
              compress ? "6" : "0",
              outputPath,
            ];
          case "avif":
            return [
              ...baseArgs,
              "-c:v",
              "libaom-av1",
              "-b:v",
              "0",
              "-crf",
              compress ? "36" : "18",
              "-pix_fmt",
              "yuva420p",
              outputPath,
            ];
          case "apng":
            return [
              ...baseArgs,
              "-plays",
              "0",
              "-pix_fmt",
              "rgba",
              "-compression_level",
              compress ? "9" : "0",
              "-f",
              "apng",
              outputPath,
            ];
          case "webm":
            return [
              ...baseArgs,
              "-c:v",
              "libvpx-vp9",
              "-pix_fmt",
              "yuva420p",
              "-b:v",
              "0",
              "-crf",
              compress ? "36" : "18",
              "-auto-alt-ref",
              "0",
              "-an",
              outputPath,
            ];
          default:
            return [...baseArgs, outputPath];
        }
      };

      for (const format of formatsToEncode) {
        if (!isFormatActive(format.key, options)) {
          onProcessing(format.key, false, jobId);
          continue;
        }

        const outputPath = `${dir}/out.${format.ext}`;
        try {
          await ffmpeg.exec(buildArgs(format.key, outputPath));
          const data = await ffmpeg.readFile(outputPath);
          const bytes = data instanceof Uint8Array ? Uint8Array.from(data) : new TextEncoder().encode(String(data));
          const blob = new Blob([bytes], { type: format.mime });
          const url = URL.createObjectURL(blob);
          const baseName = getFileBaseName(sources[0].name);

          if (isFormatActive(format.key, options)) {
            onResult(
              format.key,
              {
                blob,
                url,
                name: `${baseName}.${format.ext}`,
                size: blob.size,
                originalSize: blob.size,
              },
              jobId
            );
          } else {
            URL.revokeObjectURL(url);
          }
        } catch (err) {
          console.warn(`${format.name} encoding failed:`, err);
          if (isFormatActive(format.key, options)) {
            onError?.(format.key, err instanceof Error ? err.message : `${format.name} encoding failed`, jobId);
          }
        } finally {
          await safeUnlink(outputPath);
          onProcessing(format.key, false, jobId);
        }
      }
    } catch (err) {
      console.error("Failed to create FFmpeg animations:", err);
      formatsToEncode.forEach((format) => {
        if (isFormatActive(format.key, options)) {
          onError?.(format.key, err instanceof Error ? err.message : "Failed to create FFmpeg animations", jobId);
        }
        onProcessing(format.key, false, jobId);
      });
    } finally {
      await Promise.all(framePaths.map((path) => safeUnlink(path)));
      try {
        await ffmpeg.deleteDir(dir);
      } catch {
        // Directory cleanup is best-effort after FFmpeg failures.
      }
      disposeAnimationEncoders();
    }
  });
};

export const createAnimations = async (options: CreateAnimationsOptions) => {
  if (options.sources.length < 2) return;
  if (!isActive(options)) return;

  await Promise.all([
    createAnimatedGif(options),
    createFfmpegAnimations(options),
  ]);
};

export type { AnimationFormatKey, CreateAnimationsOptions };
